import React from "react";
import { addedScopeLabels, groupCalendarRows } from "../../utils/assessmentCalendarRules";
import CalendarVendorSponsorLine from "./CalendarVendorSponsorLine";
import CalendarRowBadgeStack from "./CalendarRowBadgeStack";
import CalendarRequestRelationshipConnector from "./CalendarRequestRelationshipConnector";
import {
  capabilityNames,
  formatCalendarLocation,
  formatOnsiteRemote,
  formatRequestScheduleLine,
  providerInitial,
  resolveProviderDisplayName,
  resolveSponsorDisplayName,
} from "../../utils/calendarRowDisplay";

const CalendarDayHoverPopup = ({ rows, isAdmin, today }) => {
  const groups = groupCalendarRows(rows);

  return (
    <div className="relative isolate">
      <div className="flex max-h-[min(24rem,calc(100vh-6rem))] w-[30rem] max-w-[min(30rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white px-2 pb-2 pt-1 text-left shadow-xl ring-1 ring-black/5">
        <div className="divide-y divide-gray-100 overflow-x-hidden overflow-y-auto bg-white">
          {groups.map(({ parent, subrows }) => (
            <div key={`${parent.vqa_id}-${parent.sponsor_id || "parent"}`} className="py-0.5">
              <HoverPopupRow row={parent} isAdmin={isAdmin} today={today} />
              {subrows.map((row) => (
                <HoverPopupRow
                  key={`${row.vqa_id}-${row.join_vqa_request_id || row.sponsor_id}`}
                  row={row}
                  isAdmin={isAdmin}
                  today={today}
                  isSubrow
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2" aria-hidden="true">
        <div className="h-0 w-0 border-b-4 border-l-4 border-r-4 border-transparent border-b-gray-200" />
        <div className="absolute bottom-0 left-1/2 h-0 w-0 -translate-x-1/2 translate-y-px border-b-4 border-l-4 border-r-4 border-transparent border-b-white" />
      </div>
    </div>
  );
};

function HoverPopupRow({ row, isAdmin, today, isSubrow = false }) {
  const metaParts = [formatOnsiteRemote(row.vqa_type), formatCalendarLocation(row)].filter(
    (part) => part && part !== "—" && part !== "Location unavailable"
  );

  return (
    <div className={`px-1 py-2 ${isSubrow ? "ml-10 border-l-2 border-gray-200 pl-3" : ""}`}>
      <div className="flex min-w-0 items-start gap-2.5 text-left">
        {!isSubrow ? (
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700"
            aria-hidden="true"
          >
            {providerInitial(resolveProviderDisplayName(row))}
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-1">
            {isSubrow ? (
              <div className="flex min-w-0 flex-1 basis-[min(100%,14rem)] items-center gap-1 pr-1">
                <CalendarRequestRelationshipConnector />
                <p className="break-words text-xs font-medium text-gray-900" title={resolveSponsorDisplayName(row)}>
                  {resolveSponsorDisplayName(row)}
                </p>
              </div>
            ) : (
              <div className="min-w-0 flex-1 basis-[min(100%,14rem)] pr-1">
                <CalendarVendorSponsorLine row={row} compact />
              </div>
            )}
            <CalendarRowBadgeStack
              row={row}
              isAdmin={isAdmin}
              today={today}
              className="basis-full justify-start sm:ml-auto sm:basis-auto sm:justify-end"
            />
          </div>
          <div className="mt-1 space-y-0.5">
            <HoverPopupScheduleMeta row={row} showAuditor={isAdmin} />
            {metaParts.length > 0 ? (
              <p className="break-words text-xs text-gray-500">{metaParts.join(" · ")}</p>
            ) : null}
            <HoverScopeChips row={row} isSubrow={isSubrow} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HoverPopupScheduleMeta({ row, showAuditor }) {
  const line = formatRequestScheduleLine(row, { showAuditor });
  const segments = line.split(" | ").filter(Boolean);
  if (!segments.length) return null;

  return (
    <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs text-gray-500">
      {segments.map((segment, index) => (
        <React.Fragment key={`${segment}-${index}`}>
          {index > 0 ? (
            <span className="text-gray-300" aria-hidden="true">
              |
            </span>
          ) : null}
          <span className="break-words">{segment}</span>
        </React.Fragment>
      ))}
    </p>
  );
}

function HoverScopeChips({ row, isSubrow }) {
  const labels = isSubrow ? addedScopeLabels(row) : capabilityNames(row);
  if (!labels.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {isSubrow ? (
        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
          Added
        </span>
      ) : null}
      {labels.map((label) => (
        <span
          key={label}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export default CalendarDayHoverPopup;
